import type { ComponentType, PropsWithChildren, ReactElement } from 'react';

import {
  SheetProvider,
  ModalProvider,
  ModalRoot,
  reactSelectQueries,
} from '@procurify/ui'
import { theme } from '@procurify/ui-theme'
import { defaultStore } from '@procurify/utils'
import * as buttonQueries from '@procurify/utils/src/testing/rtl-queries/button-queries'
import * as priceQueries from '@procurify/utils/src/testing/rtl-queries/price-queries'
import { withinTable } from '@procurify/utils/src/testing/rtl-queries/table-queries'
import {
  queries,
  render,
  type RenderResult,
  screen,
  within,
} from '@testing-library/react'
import {
  renderHook,
  type RenderHookOptions,
  type RenderHookResult,
} from '@testing-library/react-hooks'
import { createMemoryHistory, type MemoryHistory } from 'history'
import { RawIntlProvider } from 'react-intl'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom'
import {
  StyleSheetManager,
  type StylisPlugin,
  ThemeProvider,
} from 'styled-components'

import { UIProvider } from '@webapp/app/providers/UIProvider'
import configureStore from '@webapp/app/store/configureStore'
import { type RootState } from '@webapp/app/store/reducers'
import { DEFAULT_FULL_LOCALE, generateIntl } from '@webapp/core/i18n'
import { PaginationProvider } from '@webapp/core/providers/Pagination/PaginationProvider'
import { initializeQueryClient } from './queryClient'
import { history as browserHistory } from '../utils/'

/**
 * Filtering out all styles that react-testing-library doesn't need to remain
 * functional (ie. toBeVisible()). This greately increases the performance of our tests
 */
const ALLOWED_RULES = ['display', 'visibility', 'pointer-events', 'opacity']
const simplifiedStylesPlugin: StylisPlugin = (context, content) => {
  if (context === 1) {
    if (
      !ALLOWED_RULES.some((rule) => content.toString().startsWith(`${rule}:`))
    ) {
      return ''
    }
  }

  return undefined
}

const WebappProviders = <P extends object>(
  initialStore: (state: RootState) => RootState,
  history: MemoryHistory
): ComponentType<P> => {
  const queryClient = initializeQueryClient()
  let store = { ...defaultStore } as RootState

  if (initialStore) {
    store = initialStore(store)
  }

  const mockedStore = configureStore(store, browserHistory)

  // Tests will use default language, and defaultMessages
  const intl = generateIntl({
    locale: DEFAULT_FULL_LOCALE,
    defaultLocale: DEFAULT_FULL_LOCALE,
    wrapRichTextChunksInFragment: true,
    messages: {},
  })

  return ({ children }: PropsWithChildren<any>) => {
    return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <StyleSheetManager
            stylisPlugins={[simplifiedStylesPlugin]}
            disableVendorPrefixes
          >
            <Provider store={mockedStore}>
              <RawIntlProvider value={intl}>
                <Router history={history}>
                  <UIProvider>
                    <PaginationProvider>
                      <ModalProvider>
                        <SheetProvider>
                          <ModalRoot />
                          {children}
                        </SheetProvider>
                      </ModalProvider>
                    </PaginationProvider>
                  </UIProvider>
                </Router>
              </RawIntlProvider>
            </Provider>
          </StyleSheetManager>
        </ThemeProvider>
      </QueryClientProvider>
    )
  };
}

export interface RenderOptions {
  route?: string
  history?: MemoryHistory
  initialStore?: (state: RootState) => RootState
}

const extendQueries = { ...queries, ...reactSelectQueries }

const renderWithProviders = <P extends object>(
  ui: ReactElement<P>,
  renderOptions: RenderOptions = {}
) => {
  const options: RenderOptions = renderOptions ?? {}

  const {
    route = '/',
    history = createMemoryHistory({ initialEntries: [route] }),
    initialStore,
  } = options

  const renderResults = render(ui, {
    wrapper: WebappProviders(initialStore, history),
    queries: extendQueries,
  }) as RenderResult<typeof extendQueries>

  return {
    ...renderResults,
    history,
  }
}

/**
 * Extend `screen` with Select queries
 */

const customQueries = {
  ...queries,
  ...reactSelectQueries,
  ...buttonQueries,
  ...priceQueries,
}

const extendedScreen = {
  ...screen,
  ...within(document.body, customQueries),
  /**
   * @deprecated Do not use this query until the performance is fixed
   */
  getByRole: screen.getByRole,
  /**
   * @deprecated Do not use this query until the performance is fixed
   */
  getAllByRole: screen.getAllByRole,
  /**
   * @deprecated Do not use this query until the performance is fixed
   */
  queryByRole: screen.queryByRole,
  /**
   * @deprecated Do not use this query until the performance is fixed
   */
  queryAllByRole: screen.queryAllByRole,
  getModal: (): HTMLElement => document.body.querySelector('[role=dialog]'),
}

const extendedWithin = (container: HTMLElement | Element) =>
  within(container as HTMLElement, {
    ...queries,
    ...reactSelectQueries,
    ...buttonQueries,
    ...priceQueries,
  })

/**
 * Render hook with Router provider
 */

const HookRouterWrapper = <P extends object>(
  history: MemoryHistory
): ComponentType<P> => {
  const queryClient = initializeQueryClient()

  return ({ children }: PropsWithChildren<any>) => {
    return (
      <QueryClientProvider client={queryClient}>
        <Router history={history}>{children}</Router>
      </QueryClientProvider>
    )
  };
}

export const renderHookWithRouter = <P extends object, R = any>(
  callback: (props: P) => R,
  options: RenderHookOptions<P> & { history?: MemoryHistory } = {}
): RenderHookResult<P, R> & {
  history: MemoryHistory
} => {
  const { history = createMemoryHistory(), ...hookOptions } = options

  return {
    ...renderHook(callback, {
      wrapper: HookRouterWrapper<P>(history),
      ...hookOptions,
    }),
    history,
  }
}

const HookReduxWrapper = <P extends object>(
  initialStore?: (state: RootState) => RootState
): ComponentType<P> => {
  const queryClient = initializeQueryClient()

  let store = { ...defaultStore } as RootState

  if (initialStore) {
    store = initialStore(store)
  }

  const mockedStore = configureStore(store, browserHistory)

  return ({ children }: PropsWithChildren<any>) => {
    return (
      <QueryClientProvider client={queryClient}>
        <Provider store={mockedStore}>{children}</Provider>
      </QueryClientProvider>
    )
  };
}

export const renderHookWithRedux = <P extends object, R = any>(
  callback: (props: P) => R,
  options: RenderHookOptions<P> & {
    initialStore?: (state: RootState) => RootState
  } = {}
): RenderHookResult<P, R> => {
  const { initialStore, ...hookOptions } = options

  return {
    ...renderHook(callback, {
      wrapper: HookReduxWrapper<P>(initialStore),
      ...hookOptions,
    }),
  }
}

export const renderHookWithAllProviders = <P extends object, R = any>(
  callback: (props: P) => R,
  options: RenderHookOptions<P> & {
    initialStore?: (state: RootState) => RootState
    history?: MemoryHistory
  } = {}
): RenderHookResult<P, R> & {
  history: MemoryHistory
} => {
  const {
    initialStore,
    history = createMemoryHistory(),
    ...hookOptions
  } = options

  return {
    ...renderHook(callback, {
      wrapper: WebappProviders<P>(initialStore, history),
      ...hookOptions,
    }),
    history,
  }
}

export * from '@testing-library/react'
export {
  renderWithProviders as render,
  renderWithProviders as renderWithRouter,
  extendedScreen as screen,
  extendedWithin as within,
  extendedWithin as getQueriesForElement,
  withinTable,
}
